import React, { type ReactNode } from 'react';
import { notification } from 'antd-v5';
import {
    AlarmClock,
    Button,
    CrossIcon,
    DownloadFile,
    EditDocIcon,
    InfoCircleFil,
    LinkIcon,
    RefreshCCW,
    ServerIcon,
} from 'asper_design_library';
import { trackEventMixpanel } from 'utils/mixpanel';

/** App login route (Auth0 / main entry). */
const LOGIN_PATH = '/';

const HTTP_STATUS_FROM_MESSAGE = /(?:Error:|SSE connection error:)\s*(\d{3})\b/i;

const ICON_SIZE = 20;
const ICON_MUTED = 'rgba(0,0,0,0.65)';

export type NotifyAskAsperApiErrorOptions = {
    /** Use workspace export copy, icon, and optional Retry for failed downloads. */
    context?: 'export';
    onRetry?: () => void;
};

function parseHttpStatus(error: unknown): number | undefined {
    if (!(error instanceof Error)) return undefined;
    const match = error.message.match(HTTP_STATUS_FROM_MESSAGE);
    if (match) return parseInt(match[1], 10);
    return undefined;
}

function isNetworkError(error: unknown): boolean {
    if (error == null) return false;
    const err = error instanceof Error ? error : new Error(String(error));
    const name = (err.name || '').toLowerCase();
    const msg = (err.message || '').toLowerCase();
    if (typeof navigator !== 'undefined' && navigator.onLine === false) return true;
    if (name === 'typeerror' && msg.includes('failed to fetch')) return true;
    if (name === 'networkerror' || msg.includes('networkerror')) return true;
    if (msg.includes('load failed') || msg.includes('network request failed')) return true;
    if (err instanceof TypeError && msg.includes('fetch')) return true;
    return false;
}

function isSessionExpiredError(error: unknown, status: number | undefined): boolean {
    if (status === 401) return true;
    if (!(error instanceof Error)) return false;
    return error.message.toLowerCase().includes('no access token');
}

function isQueryTooLongError(error: unknown, status: number | undefined): boolean {
    if (status === 413) return true;
    if (!(error instanceof Error)) return false;
    const msg = error.message.toLowerCase();
    return (
        msg.includes('too long') ||
        msg.includes('character limit') ||
        msg.includes('max length') ||
        msg.includes('payload too large')
    );
}

type Category = 'Network' | 'Auth' | 'Input' | 'Workspace' | 'App';

type ResolvedToast = {
    code: string;
    category: Category;
    categoryClassName: string;
    headline: string;
    message: string;
    icon: ReactNode;
    primaryAction?: { label: string; onClick: () => void };
};

const CATEGORY_STYLES: Record<Category, string> = {
    Network: 'bg-rose-100 text-rose-900',
    Auth: 'bg-amber-100 text-amber-950',
    Input: 'bg-orange-100 text-orange-950',
    Workspace: 'bg-emerald-100 text-emerald-900',
    App: 'bg-sky-100 text-sky-950',
};

function resolveToast(error: unknown, options?: NotifyAskAsperApiErrorOptions): ResolvedToast {
    const status = parseHttpStatus(error);
    const network = isNetworkError(error);
    const exportCtx = options?.context === 'export';

    if (network) {
        return {
            code: 'ERR_OFFLINE',
            category: 'Network',
            categoryClassName: CATEGORY_STYLES.Network,
            headline: 'No Internet Connection',
            message: 'You appear to be offline. Check your connection and try again.',
            icon: <LinkIcon size={ICON_SIZE} color={ICON_MUTED} />,
        };
    }

    if (isSessionExpiredError(error, status)) {
        return {
            code: 'ERR_SESSION_EXPIRED',
            category: 'Auth',
            categoryClassName: CATEGORY_STYLES.Auth,
            headline: 'Session Expired',
            message: 'Your session has expired. Please sign in again.',
            icon: <InfoCircleFil color="#CA8A04" />,
            primaryAction: {
                label: 'Sign In',
                onClick: () => {
                    globalThis.location.assign(LOGIN_PATH);
                },
            },
        };
    }

    if (exportCtx) {
        return {
            code: 'ERR_EXPORT_FAILED',
            category: 'Workspace',
            categoryClassName: CATEGORY_STYLES.Workspace,
            headline: 'Export Failed',
            message: 'We could not generate the export. Please try again.',
            icon: <DownloadFile size={ICON_SIZE} color={ICON_MUTED} />,
            primaryAction: options?.onRetry
                ? {
                      label: 'Retry',
                      onClick: () => {
                          options.onRetry?.();
                      },
                  }
                : undefined,
        };
    }

    if (isQueryTooLongError(error, status)) {
        return {
            code: 'ERR_QUERY_TOO_LONG',
            category: 'Input',
            categoryClassName: CATEGORY_STYLES.Input,
            headline: 'Query Too Long',
            message: 'Your question exceeds the character limit. Try a shorter prompt.',
            icon: <EditDocIcon size={ICON_SIZE} color={ICON_MUTED} />,
        };
    }

    if (status === 408) {
        return {
            code: 'ERR_TIMEOUT',
            category: 'Network',
            categoryClassName: CATEGORY_STYLES.Network,
            headline: 'Request Timed Out',
            message: 'The server took too long to respond. Please retry.',
            icon: <AlarmClock size={ICON_SIZE} color={ICON_MUTED} />,
            primaryAction: options?.onRetry
                ? {
                      label: 'Retry',
                      onClick: () => {
                          options.onRetry?.();
                      },
                  }
                : undefined,
        };
    }

    if (status === 502 || status === 503 || status === 504) {
        return {
            code: 'ERR_SERVER_DOWN',
            category: 'Network',
            categoryClassName: CATEGORY_STYLES.Network,
            headline: 'Service Unavailable',
            message: 'Our servers are temporarily unavailable. We are on it.',
            icon: <ServerIcon size={ICON_SIZE} color={ICON_MUTED} />,
        };
    }

    if (status !== undefined) {
        switch (status) {
            case 400:
                return {
                    code: `ERR_${status}`,
                    category: 'Input',
                    categoryClassName: CATEGORY_STYLES.Input,
                    headline: 'Bad Request',
                    message: 'The request could not be understood. Check your input and try again.',
                    icon: <EditDocIcon size={ICON_SIZE} color={ICON_MUTED} />,
                };
            case 403:
                return {
                    code: 'ERR_FORBIDDEN',
                    category: 'Auth',
                    categoryClassName: CATEGORY_STYLES.Auth,
                    headline: 'Forbidden',
                    message: 'You do not have permission for this action.',
                    icon: <InfoCircleFil color="#CA8A04" />,
                };
            case 404:
                return {
                    code: 'ERR_NOT_FOUND',
                    category: 'App',
                    categoryClassName: CATEGORY_STYLES.App,
                    headline: 'Not Found',
                    message: 'The requested resource was not found.',
                    icon: <InfoCircleFil color="#44338A" />,
                };
            case 409:
                return {
                    code: 'ERR_CONFLICT',
                    category: 'Input',
                    categoryClassName: CATEGORY_STYLES.Input,
                    headline: 'Conflict',
                    message: 'The request could not be completed due to a conflict.',
                    icon: <InfoCircleFil color="#44338A" />,
                };
            case 429:
                return {
                    code: 'ERR_RATE_LIMIT',
                    category: 'Network',
                    categoryClassName: CATEGORY_STYLES.Network,
                    headline: 'Too Many Requests',
                    message: 'Please wait a moment and try again.',
                    icon: <AlarmClock size={ICON_SIZE} color={ICON_MUTED} />,
                };
            case 500:
                return {
                    code: 'ERR_INTERNAL',
                    category: 'Network',
                    categoryClassName: CATEGORY_STYLES.Network,
                    headline: 'Server Error',
                    message: 'Something went wrong on the server. Please try again later.',
                    icon: <ServerIcon size={ICON_SIZE} color={ICON_MUTED} />,
                };
            default:
                if (status >= 500) {
                    return {
                        code: `ERR_${status}`,
                        category: 'Network',
                        categoryClassName: CATEGORY_STYLES.Network,
                        headline: 'Server Error',
                        message: `Something went wrong (${status}). Please try again later.`,
                        icon: <ServerIcon size={ICON_SIZE} color={ICON_MUTED} />,
                    };
                }
                if (status >= 400) {
                    return {
                        code: `ERR_${status}`,
                        category: 'App',
                        categoryClassName: CATEGORY_STYLES.App,
                        headline: 'Request failed',
                        message: `We could not complete your request (error ${status}).`,
                        icon: <InfoCircleFil color="#44338A" />,
                    };
                }
                return {
                    code: `ERR_${status}`,
                    category: 'App',
                    categoryClassName: CATEGORY_STYLES.App,
                    headline: 'Something Went Wrong',
                    message: 'An unexpected error occurred. Please try again.',
                    icon: <RefreshCCW size={ICON_SIZE} color={ICON_MUTED} />,
                };
        }
    }

    if (error instanceof Error && error.message) {
        return {
            code: 'ERR_APP_CRASH',
            category: 'App',
            categoryClassName: CATEGORY_STYLES.App,
            headline: 'Something Went Wrong',
            message: error.message,
            icon: <RefreshCCW size={ICON_SIZE} color={ICON_MUTED} />,
            primaryAction: {
                label: 'Refresh',
                onClick: () => {
                    globalThis.location.reload();
                },
            },
        };
    }

    return {
        code: 'ERR_APP_CRASH',
        category: 'App',
        categoryClassName: CATEGORY_STYLES.App,
        headline: 'Something Went Wrong',
        message: 'An unexpected error occurred. Please refresh the page.',
        icon: <RefreshCCW size={ICON_SIZE} color={ICON_MUTED} />,
        primaryAction: {
            label: 'Refresh',
            onClick: () => {
                globalThis.location.reload();
            },
        },
    };
}

const AskAsperToastMessage: React.FC<{ spec: ResolvedToast; onClose: () => void }> = ({ spec, onClose }) => {
    return (
        <div
            className="flex flex-col gap-2 border rounded-lg px-3 py-2 font-sans min-w-[24rem]"
            data-test-id="ask-asper-notification-container"
        >
            <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xs font-mono text-black/45 shrink-0">{spec.code}</span>
                    <span
                        className={`rounded px-2 py-0.5 text-xs font-medium shrink-0 ${spec.categoryClassName}`}
                        data-test-id="ask-asper-notification-category"
                    >
                        {spec.category}
                    </span>
                </div>
                <CrossIcon
                    size={16}
                    color="#00000073"
                    className="cursor-pointer shrink-0"
                    onClick={onClose}
                />
            </div>
            <div className="flex gap-3 items-start">
                <div className="shrink-0 pt-0.5" data-test-id="ask-asper-notification-icon">
                    {spec.icon}
                </div>
                <div className="flex flex-col gap-1 min-w-0 flex-1">
                    <div className="text-black88 text-sm font-medium leading-snug">{spec.headline}</div>
                    <div className="text-black/65 text-sm leading-snug">{spec.message}</div>
                    {spec.primaryAction && (
                        <Button
                            type="primary"
                            size="small"
                            className="self-start mt-1"
                            onClick={() => {
                                onClose();
                                spec.primaryAction?.onClick();
                            }}
                            data-test-id="ask-asper-notification-action"
                        >
                            {spec.primaryAction.label}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

/**
 * Shows a spec-style error notification (bottom-right): code, category tag, headline, body, icon, optional action.
 * Auto-dismiss after 5 seconds unless the user interacts.
 */
export function notifyAskAsperApiError(error: unknown, options?: NotifyAskAsperApiErrorOptions): void {
    const spec = resolveToast(error, options);
    trackEventMixpanel(`ask_asper_${spec.code.toLowerCase()}`, {
        err_code: spec.code,
        category: spec.category,
        ...(options?.context === 'export' ? { context: 'export' as const } : {}),
    });

    try {
        const key = `ask-asper-api-error-${Date.now()}`;
        const onClose = () => notification.destroy(key);
        notification.open({
            key,
            message: <AskAsperToastMessage spec={spec} onClose={onClose} />,
            duration: 5,
            placement: 'bottomRight',
            closeIcon: false,
            className: 'notifyApp min-w-[26rem]',
        });
    } catch {
        console.error('[AskAsper] API error (notification unavailable):', error);
    }
}

const ArtifactLockIcon = () => (
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#F3EFFF]">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
            <rect x="4" y="10.5" width="16" height="10" rx="2.5" stroke="#5F49BC" strokeWidth="1.7" />
            <path d="M7.5 10.5V7.5a4.5 4.5 0 0 1 9 0v3" stroke="#5F49BC" strokeWidth="1.7" strokeLinecap="round" />
            <circle cx="12" cy="15.3" r="1.4" fill="#5F49BC" />
        </svg>
    </span>
);

const AskAsperArtifactUnavailableToast: React.FC<{ onClose: () => void }> = ({ onClose }) => (
    <div
        className="flex items-start gap-3 border rounded-lg px-3 py-2 font-sans min-w-[24rem]"
        data-test-id="ask-asper-artifact-unavailable-container"
    >
        <ArtifactLockIcon />
        <div className="flex flex-col gap-1 min-w-0 flex-1">
            <div className="text-black88 text-sm font-medium leading-snug">
                This artifact is not available to you.
            </div>
            <div className="text-black/65 text-sm leading-snug">
                It may belong to another user or you may no longer have access.
            </div>
        </div>
        <CrossIcon
            size={16}
            color="#00000073"
            className="cursor-pointer shrink-0"
            onClick={onClose}
        />
    </div>
);

/**
 * Shown when a user opens an export deep link that belongs to a different user.
 * Bottom-right toast matching the "artifact not available" design; auto-dismisses after 5s.
 */
export function notifyAskAsperArtifactUnavailable(): void {
    trackEventMixpanel('ask_asper_deeplink_artifact_unavailable', {
        err_code: 'ERR_ARTIFACT_UNAVAILABLE',
        category: 'Auth',
    });

    try {
        const key = 'ask-asper-artifact-unavailable';
        const onClose = () => notification.destroy(key);
        notification.open({
            key,
            message: <AskAsperArtifactUnavailableToast onClose={onClose} />,
            duration: 5,
            placement: 'bottomRight',
            closeIcon: false,
            className: 'notifyApp min-w-[26rem]',
        });
    } catch {
        console.error('[AskAsper] Artifact unavailable (notification unavailable)');
    }
}
